export interface TrainingModuleDefinition {
  id: string;
  title: string;
  audience: string[];
  durationMinutes: number;
  outcome: string;
  topics: string[];
  lessons: {
    title: string;
    content: string;
    practice: string;
  }[];
  knowledgeChecks: {
    question: string;
    options: string[];
    answer: number;
    explanation: string;
  }[];
  sourceLabel: string;
  sourceUrl: string;
}

export const TRAINING_LIBRARY: TrainingModuleDefinition[] = [
  {
    id: "DSM-TR-CDM-CLIENT",
    title: "Malaysia CDM 2024: client leadership",
    audience: ["Client", "Project director", "Developer"],
    durationMinutes: 35,
    outcome: "Set adequate arrangements, appoint dutyholders and verify the project evidence trail.",
    topics: ["Regulations 4–9", "Pre-construction information", "Notification", "Safety and Health File"],
    lessons: [
      {
        title: "Lead through project arrangements",
        content:
          "The client shapes the conditions for safe delivery by allowing adequate time and resources, defining the project brief, supplying pre-construction information and keeping arrangements under review.",
        practice:
          "Compare the current programme and budget with the highest-risk work packages. Record where time, information or specialist resources are still missing.",
      },
      {
        title: "Make appointments and assurance visible",
        content:
          "Where more than one contractor is foreseeable, written principal designer and principal contractor appointments are early control decisions. The client then takes reasonable steps to verify delivery rather than transferring away responsibility.",
        practice:
          "Open the regulation engine, assign Regulations 7 and 8, and attach appointment or notification evidence.",
      },
    ],
    knowledgeChecks: [
      {
        question: "When should principal appointments be made?",
        options: ["After construction starts", "As early as practicable", "Only after notification"],
        answer: 1,
        explanation: "Regulation 7 requires appointments as early as practicable where multiple contractors are foreseeable.",
      },
      {
        question: "What makes client assurance defensible?",
        options: ["A generic policy", "Verbal confirmation", "Named actions linked to evidence"],
        answer: 2,
        explanation: "The platform’s evidence chain makes the duty, accountable owner, response and supporting record traceable.",
      },
    ],
    sourceLabel: "P.U. (A) 147/2024 · Regulations 4–9",
    sourceUrl:
      "https://dosh.gov.my/wp-content/uploads/2024/10/Peraturan-Peraturan-Keselamatan-Dan-Kesihatan-Pekerjaan-Kerja-Pembinaan-Reka-Bentuk-Dan-Pengurusan-2024.pdf#page=10",
  },
  {
    id: "DSM-TR-PTD",
    title: "Prevention through Design decision workshop",
    audience: ["Designer", "Principal Designer", "Engineer", "Architect"],
    durationMinutes: 50,
    outcome: "Document elimination, safer alternatives, residual risk and revision-linked decisions.",
    topics: ["General principles of prevention", "Lifecycle risk", "Option evaluation", "Residual-risk communication"],
    lessons: [
      {
        title: "Move risk decisions upstream",
        content:
          "Prevention through Design starts before method statements: eliminate foreseeable exposure through layout, specification, access, sequencing and permanent features, then reduce what cannot be eliminated.",
        practice:
          "Select one high-consequence maintenance task and record at least two design alternatives before accepting a residual control.",
      },
      {
        title: "Communicate the residual risk precisely",
        content:
          "Useful residual-risk information explains the unusual or significant hazard, the affected lifecycle stage, the design decision and the exact controlled drawing or specification revision.",
        practice:
          "Create a design-risk record and connect it to the revision that carries the control.",
      },
    ],
    knowledgeChecks: [
      {
        question: "Which option should be considered first?",
        options: ["PPE", "Elimination through design", "Warning signage"],
        answer: 1,
        explanation: "Elimination is the first and strongest level in the hierarchy of control.",
      },
      {
        question: "What is essential for residual-risk communication?",
        options: ["A colour only", "A controlled design reference", "A generic hazard list"],
        answer: 1,
        explanation: "The recipient must know where the decision is carried and which revision applies.",
      },
    ],
    sourceLabel: "CDM Regulation 11 and NIOSH PtD toolkit",
    sourceUrl: "https://www.cdc.gov/niosh/publications/numbered/2024-124.html",
  },
  {
    id: "DSM-TR-HIRARC",
    title: "DOSH HIRARC practitioner",
    audience: ["HSE", "Supervisor", "Contractor", "Designer"],
    durationMinutes: 60,
    outcome: "Complete a defensible HIRARC with hierarchy-based controls, PIC and residual risk.",
    topics: ["Activity classification", "Hazard identification", "5×5 matrix", "Hierarchy of control", "Review triggers"],
    lessons: [
      {
        title: "Separate hazard, consequence and risk",
        content:
          "Describe the activity and hazard specifically, identify who may be harmed and state a credible consequence before assigning likelihood and severity. The DOSH 5×5 score is likelihood multiplied by severity.",
        practice:
          "Create one HIRARC row for a real work activity and test whether a colleague could recognise the exposure without further explanation.",
      },
      {
        title: "Re-score only after controls are defined",
        content:
          "Recommended controls must be specific, owned and time-bound. Residual likelihood and severity describe the condition after those controls are implemented, not an aspirational target.",
        practice:
          "Use the hierarchy to improve a medium or high risk, assign a PIC and set a review trigger.",
      },
    ],
    knowledgeChecks: [
      {
        question: "What is the score for likelihood 3 and severity 5?",
        options: ["8", "15", "25"],
        answer: 1,
        explanation: "The HIRARC score is likelihood × severity, so 3 × 5 = 15.",
      },
      {
        question: "When is residual risk scored?",
        options: ["Before controls", "After defined controls", "Only after an incident"],
        answer: 1,
        explanation: "Residual risk represents the expected position after the selected controls are implemented.",
      },
    ],
    sourceLabel: "DOSH HIRARC 2008",
    sourceUrl:
      "https://dosh.gov.my/wp-content/uploads/2026/05/Guidelines-for-Hazard-Identification-Risk-Assessment-and-Risk-Control-HIRARC-2008.pdf#page=24",
  },
  {
    id: "DSM-TR-CRITICAL",
    title: "Critical control verification and bowties",
    audience: ["HSE", "Principal Contractor", "Supervisor", "Manager"],
    durationMinutes: 45,
    outcome: "Define control performance standards and verify the controls that prevent fatal events.",
    topics: ["Major accident scenarios", "Preventive barriers", "Mitigative barriers", "Degradation factors", "Verification"],
    lessons: [
      {
        title: "Identify the controls that must not fail",
        content:
          "A critical control directly prevents a major accident scenario or materially limits its consequence. A bowtie separates preventive controls on the threat side from mitigative controls after the top event.",
        practice:
          "Choose one fatal-risk scenario and distinguish the critical controls from supporting procedures.",
      },
      {
        title: "Write a verifiable performance standard",
        content:
          "The standard states what the control must do, under what conditions and to what measurable criterion. Verification must test the control itself rather than merely confirm paperwork exists.",
        practice:
          "Write one pass/fail verification method, name its owner and set a frequency based on how quickly the control can degrade.",
      },
    ],
    knowledgeChecks: [
      {
        question: "What distinguishes a critical control?",
        options: ["It is easy to audit", "It directly prevents or mitigates a major event", "It appears in every procedure"],
        answer: 1,
        explanation: "Criticality comes from the control’s direct role in preventing or limiting the major event.",
      },
      {
        question: "What should verification test?",
        options: ["The control’s actual performance", "Attendance only", "The document title"],
        answer: 0,
        explanation: "Verification provides evidence that the barrier will perform to its defined standard.",
      },
    ],
    sourceLabel: "Energy Institute bowtie framework",
    sourceUrl:
      "https://www.energyinst.org/technical/publications/topics/human-and-organisational-factors/bow-ties-in-risk-management-a-concept-book-for-process-safety",
  },
  {
    id: "DSM-TR-GOLDEN-THREAD",
    title: "Safety information golden thread",
    audience: ["Document Controller", "Designer", "Principal Designer", "Client"],
    durationMinutes: 40,
    outcome: "Maintain revision-aware, retrievable and protected safety information through handover.",
    topics: ["ISO 19650-6", "Revision control", "Change impact", "BCF issues", "Safety and Health File"],
    lessons: [
      {
        title: "Preserve the safety-information chain",
        content:
          "A golden thread connects the decision to the exact document revision, its author, issue state, change summary, approval and retained file. Superseded information remains identifiable instead of disappearing.",
        practice:
          "Register a revision, describe its safety-relevant change and connect the affected design risks.",
      },
      {
        title: "Control electronic records",
        content:
          "CDM Regulation 3(2) recognises electronic records where they can be retrieved, reproduced and protected from loss or unauthorised interference. Private storage, checksums and an audit trail support those properties.",
        practice:
          "Upload a controlled file, verify its SHA-256 value and open it through a short-lived signed link.",
      },
    ],
    knowledgeChecks: [
      {
        question: "What should happen to an issued revision?",
        options: ["Delete it when replaced", "Retain and mark it superseded", "Rename it without history"],
        answer: 1,
        explanation: "Retention of superseded information preserves the decision and change trail.",
      },
      {
        question: "What does a checksum help demonstrate?",
        options: ["File integrity", "User competence", "Legal applicability"],
        answer: 0,
        explanation: "The checksum provides a stable fingerprint that exposes file changes.",
      },
    ],
    sourceLabel: "ISO 19650-6:2025 and CDM Regulation 3(2)",
    sourceUrl: "https://www.iso.org/standard/82705.html",
  },
  {
    id: "DSM-TR-INCIDENT",
    title: "Incident learning for designers",
    audience: ["Designer", "HSE", "Principal Designer"],
    durationMinutes: 35,
    outcome: "Translate incident causes and failed barriers into project-specific design actions.",
    topics: ["Cause analysis", "Barrier failure", "Transferability", "Design-out action", "Evidence close-out"],
    lessons: [
      {
        title: "Look beyond the immediate act",
        content:
          "A useful investigation distinguishes what happened, immediate causes, underlying organisational or design conditions and the controls that were absent, failed or bypassed.",
        practice:
          "Record an incident or external case and name at least one design condition and one failed control.",
      },
      {
        title: "Make the learning change live work",
        content:
          "Learning has value only when its transferability is tested and it changes a risk, control, drawing, specification, training assignment or duty response with accountable close-out.",
        practice:
          "Link the lesson to a design risk and critical control, then update the affected design decision.",
      },
    ],
    knowledgeChecks: [
      {
        question: "Which is an underlying cause?",
        options: ["The worker slipped", "Access was designed without a stable landing", "The event occurred at 10am"],
        answer: 1,
        explanation: "The design condition explains why exposure existed and can support upstream prevention.",
      },
      {
        question: "When is incident learning closed?",
        options: ["When the report is filed", "When actions change and verify the live controls", "When a toolbox talk is scheduled"],
        answer: 1,
        explanation: "Closure requires the lesson to change relevant project controls and be verified.",
      },
    ],
    sourceLabel: "NIOSH FACE",
    sourceUrl: "https://www.cdc.gov/niosh/face/about/index.html",
  },
];
